import type { Maybe, TargetingRule, TargetingRuleDefinitionV4 } from '../../../../apis/graphql';
import { filterTermValues } from '../../../../common/utils';
import type { Nullable, TargetingTermValue } from '../../../../models';
import type { DrawerTargetingTableResult, QueryResult } from '../hooks/types';
import type { DrawerTargetingFormValues } from '../hooks/types/formValues';

// Re-exported for backward compatibility. The canonical location for these helpers is
// `app/src/common/adTargeting/` so they can be shared across pages that save ads.
export {
  computeHasInvalidCountryForAdSave,
  computeHasInvalidPublisherForAdSave,
  getAllCountryCodesMentionedInAdTargeting,
  getCountriesTargetFromAdTargetingSubmission,
  getCountryCodesFromTargetingTermValues,
  getDisplayPublisherTargetAfterAdTargetingSave,
  getExcludedCountryCodesFromTargetingTermValues,
  getPublishersFromTargetingTermValues,
  getPublisherTargetLineItem,
} from '../../../../common/adTargeting';

type TargetingTableDataType = { groupType: string; targetingTermValues: TargetingTermValue[] }[];
export type TargetingTableResult = Omit<DrawerTargetingTableResult, 'isFetching' | 'isUpdating' | 'submitForm'>;

const NAME_OF_COUNTRY_DIMENSION = 'country';

export const generateTargetingTableData = (
  adTermValues: TargetingTermValue[],
  lineItemTermValues: TargetingTermValue[]
): TargetingTableDataType => {
  return [
    {
      groupType: 'Ad Targeting',
      targetingTermValues: adTermValues,
    },
    {
      groupType: 'Line Item Targeting',
      targetingTermValues: lineItemTermValues,
    },
  ];
};

export type ValuesForMutationType = {
  targetingTermValues: {
    dimensionName: string;
    include: boolean;
    valueName: string;
  }[];
};

export const convertValuesForMutation = (newValue: TargetingTermValue[]): ValuesForMutationType => {
  const convertedValue = newValue.map(({ value, include, dimension }) => {
    return {
      dimensionName: dimension.id,
      include: include,
      valueName: value.id,
    };
  });

  return {
    targetingTermValues: convertedValue,
  };
};

// Ads have their own targeting values, and may also have line item targeting values
export const getAdTerms = (data?: QueryResult): TargetingTableResult => {
  const adNode = data?.adsV5.edges[0]?.node;

  return {
    ad: {
      termValues: filterTermValues(adNode?.targetingTermValues),
      displayPublisherTarget: adNode?.displayPublisherTarget ?? [],
      targetingRule: adNode?.targetingRule ?? null,
      hasInvalidPublisher: adNode?.hasInvalidPublisher ?? null,
      hasInvalidCountry: adNode?.hasInvalidCountry ?? null,
      countriesTarget: adNode?.countriesTarget ?? null,
    },
    lineItem: {
      termValues: filterTermValues(adNode?.lineItem?.targetingTermValues),
      displayPublisherTarget: adNode?.lineItem?.displayPublisherTarget ?? [],
      targetingRule: adNode?.lineItem?.targetingRule ?? null,
      countries: adNode?.lineItem?.countries ?? null,
    },
  };
};

// Line Items only have their own targeting values
export const getLineItemTerms = (data?: QueryResult): TargetingTableResult => {
  const lineItemNode = data?.lineItemsV5.edges[0]?.node;

  return {
    ad: {
      termValues: [],
      displayPublisherTarget: [],
      targetingRule: null,
      hasInvalidPublisher: null,
      hasInvalidCountry: null,
      countriesTarget: null,
    },
    lineItem: {
      termValues: filterTermValues(lineItemNode?.targetingTermValues),
      displayPublisherTarget: lineItemNode?.displayPublisherTarget ?? [],
      targetingRule: lineItemNode?.targetingRule ?? null,
      countries: lineItemNode?.countries ?? null,
    },
  };
};

export function filterOutCountryTarget({ value }: { value: TargetingTermValue[] }): TargetingTermValue[] {
  return value?.filter((item) => item.dimension.name.toLowerCase() !== NAME_OF_COUNTRY_DIMENSION);
}

export function getLineItemCountryTargetingTerms({ value }: { value: TargetingTermValue[] }): TargetingTermValue[] {
  return value?.filter((item) => item.dimension.name.toLowerCase() === NAME_OF_COUNTRY_DIMENSION);
}

/** Treat only explicit true as "invalid" for UI / cache alignment with Elasticsearch-backed flags. */
export function isInvalidTargetingFlagSet(value: Nullable<boolean>): boolean {
  return value === true;
}

export function stringArraysEqualAsSets(a: Nullable<string[]>, b: string[]): boolean {
  const normA = a ?? [];
  if (normA.length !== b.length) {
    return false;
  }
  const sortedA = [...normA].sort((a, b) => a.localeCompare(b));
  const sortedB = [...b].sort((a, b) => a.localeCompare(b));
  return sortedA.every((v, i) => v === sortedB[i]);
}

export function addLineItemCountryTargetingTerms(
  formValues: DrawerTargetingFormValues,
  countryTerms: TargetingTermValue[]
): DrawerTargetingFormValues {
  if (countryTerms) {
    formValues.lineItemTargetingTermValues.push(...countryTerms);
  }
  return formValues;
}

export function addPublisherToTargetingRule(
  rule: TargetingRule | null | undefined,
  publishers: string[] | undefined
): TargetingRule | null | undefined {
  if (!rule?.definition || !publishers?.length) return rule;
  const publisherTerm: TargetingRuleDefinitionV4 = {
    __typename: 'TargetingRuleDefinitionV4',
    id: null as Maybe<string>,
    name: null,
    not: false,
    operation: null,
    dimension: 'publisher',
    termList: null,
    subClass: 'com.disney.digital.ads.rule.manager.common.Include',
    valueSet: publishers,
    rangeList: null,
  };
  const newTermList: Maybe<Array<Maybe<TargetingRuleDefinitionV4>>> = [
    publisherTerm,
    ...(rule.definition?.termList ?? []),
  ];

  const definition: Maybe<TargetingRuleDefinitionV4> = {
    ...rule.definition,
    termList: newTermList,
  };

  return {
    ...rule,
    definition,
  };
}
